import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiCheck,
  FiSearch, FiAlertCircle, FiBookOpen, FiChevronRight, FiUsers,
} from 'react-icons/fi';
import { loadTable, saveRow, addRow, removeRow, getTableData } from '../../db-viewer/data/dataAccess';
import { addNotification } from '../../../lib/notifications';
import { getAccountOwnerId } from '../utils/ownerEntities';
import { entityBaseCurrency } from '../utils/baseCurrency';
import { confirmDiscardChanges, setFormDirty, registerSaveHandler, submitSaver } from '../../../lib/unsavedChanges';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import SetupHeadersTab from './SetupHeadersTab';
import TypeOfBusinessTab from './TypeOfBusinessTab';
import SetupGlAccountsTab from './SetupGlAccountsTab';
import TagInput from '../../../components/ui/TagInput';
import { recordTagUse } from '../../../lib/tagStore';
import './GLChartOfAccountsTab.css';

// =============================================
// Types
// =============================================
interface OwnerEntity {
  owner_type: 'account_owner' | 'family_member' | 'business' | 'client_personal' | 'client_business';
  owner_id: number;
  display_name: string;
  badge: string;
}

interface CoaRow {
  coa_id: number;
  account_owner_id: number;
  owner_type: string;
  owner_id: number;
  parent_coa_id: number | null;
  coa_depth: number;
  finsta_cate_id: number;
  account_number: string;
  account_name: string;
  account_description: string;
  /** other names the account is known by (comma list) */
  alias_names?: string | null;
  /** free labels to group / find accounts by (comma list) */
  tags?: string | null;
  normal_balance: 'Debit' | 'Credit';
  is_header: number;
  opening_balance: number;
  current_balance: number;
  currency: string;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

interface HeaderRow {
  header_id: number;
  header_name: string;
  header_code: string;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface CategoryRow {
  cate_id: number;
  header_id: number;
  cate_name: string;
  cate_code: string;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface SubCategoryRow {
  sub_cate_id: number;
  cate_id: number;
  sub_cate_name: string;
  sub_cate_code: string;
  sub_cate_description: string;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface CoaFormFields {
  finsta_cate_id: string;
  finsta_sub_cate_id: string;
  account_number: string;
  account_name: string;
  account_description: string;
  alias_names: string;
  tags: string;
  normal_balance: 'Debit' | 'Credit' | '';
  is_header: number;
  parent_coa_id: string;
  opening_balance: string;
  opening_balance_date: string;
  currency: string;
}

interface CoaLevel {
  inputText: string;
  selectedAccount: CoaRow | null;
  showAutocomplete: boolean;
}

// =============================================
// Constants
// =============================================
const EMPTY_FORM: CoaFormFields = {
  finsta_cate_id: '',
  finsta_sub_cate_id: '',
  account_number: '',
  account_name: '',
  account_description: '',
  alias_names: '',
  tags: '',
  normal_balance: '',
  is_header: 0,
  parent_coa_id: '',
  opening_balance: '0.00',
  opening_balance_date: '',
  currency: 'CAD',
};

/** "a,  b ,,c" → "a, b, c" (null when empty) — how alias names and tags are stored. */
function cleanList(raw: string): string | null {
  const parts = raw.split(',').map(x => x.trim()).filter(Boolean);
  return parts.length ? Array.from(new Set(parts)).join(', ') : null;
}

const NORMAL_BALANCE_BY_HEADER: Record<string, 'Debit' | 'Credit'> = {
  'Asset': 'Debit',
  'Liability': 'Credit',
  'Equity': 'Credit',
  'Income': 'Credit',
  'Expense': 'Debit',
};

const MAX_COA_LEVELS = 3;
const COA_LEVEL_LABELS = ['COA Name', 'Sub-account Name', 'Sub-sub-account Name', 'Sub-sub-sub-account Name'];

const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);

// =============================================
// Helpers
// =============================================
function getCurrentUser(usersData: Record<string, any>[]) {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null || userRow.owner_id === '' ? null : Number(userRow.owner_id),
      role: session.role || '',
      user_type_id: Number(userRow.user_type_id) || 0,
      first_name: userRow.first_name || '',
      last_name: userRow.last_name || '',
    };
  } catch { /* ignore */ }
  return null;
}

function getAccountOwnerForUser(userId: number, usersData: Record<string, any>[]): Record<string, any> | null {
  // eslint-disable-next-line eqeqeq
  const user = usersData.find((u: any) => u.user_id == userId);
  if (!user) return null;
  if (user.owner_id == null || user.owner_id === '') return user;
  // eslint-disable-next-line eqeqeq
  return usersData.find((u: any) => u.user_id == user.owner_id) || null;
}

function getOwnerEntities(accountOwnerId: number, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];

  // eslint-disable-next-line eqeqeq
  const owner = usersData.find((u: any) => u.user_id == accountOwnerId);
  if (owner) {
    entities.push({
      owner_type: 'account_owner',
      owner_id: Number(owner.user_id),
      display_name: `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || 'Account Owner',
      badge: 'Owner',
    });
  }

  usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted != 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => {
      entities.push({
        owner_type: 'family_member',
        owner_id: Number(u.user_id),
        display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family Member',
        badge: 'Family',
      });
    });

  businessesData
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == accountOwnerId && b.is_deleted != 1 && b.is_client_business != 1)
    .forEach((b: any) => {
      entities.push({
        owner_type: 'business',
        owner_id: Number(b.business_id),
        display_name: b.busi_legal_name || b.busi_operating_name || 'Business',
        badge: 'Business',
      });
    });

  const personalClients = usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted != 1 && u.relationship === 'Personal Client');

  personalClients.forEach((client: any) => {
    const clientId = Number(client.user_id);
    const clientName = `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.email || 'Client';

    entities.push({
      owner_type: 'client_personal',
      owner_id: clientId,
      display_name: clientName,
      badge: 'Client',
    });

    usersData
      // eslint-disable-next-line eqeqeq
      .filter((u: any) => u.owner_id == clientId && u.is_deleted != 1 && u.relationship !== 'Personal Client')
      .forEach((u: any) => {
        entities.push({
          owner_type: 'client_personal',
          owner_id: Number(u.user_id),
          display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Client Family',
          badge: `${clientName}'s Family`,
        });
      });

    businessesData
      // eslint-disable-next-line eqeqeq
      .filter((b: any) => b.owner_id == clientId && b.is_deleted != 1 && b.is_client_business == 1)
      .forEach((b: any) => {
        entities.push({
          owner_type: 'client_business',
          owner_id: Number(b.business_id),
          display_name: b.busi_legal_name || b.busi_operating_name || 'Client Business',
          badge: `${clientName}'s Biz`,
        });
      });
  });

  return entities;
}

function filterActiveAccounts(allAccounts: CoaRow[], accountOwnerId: number): CoaRow[] {
  // eslint-disable-next-line eqeqeq
  return allAccounts.filter(a => a.account_owner_id == accountOwnerId && a.is_deleted !== 1);
}

function filterActiveCategories(allCategories: CategoryRow[]): CategoryRow[] {
  return allCategories.filter(c => c.is_deleted !== 1);
}

function parseCurrencies(data: Record<string, any>[]): { code: string; name: string; symbol: string }[] {
  return data
    .filter((c: any) => c.is_deleted !== 1)
    .map((c: any) => ({
      code: c.currency_code || '',
      name: c.currency_name || '',
      symbol: c.currency_symbol || '',
    }))
    .sort((a: any, b: any) => a.code.localeCompare(b.code));
}

function getDescendantIds(coaId: number, allAccounts: CoaRow[]): number[] {
  const ids: number[] = [];
  const queue = [coaId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    // eslint-disable-next-line eqeqeq
    const children = allAccounts.filter(a => a.parent_coa_id == current && a.is_deleted !== 1);
    for (const c of children) {
      ids.push(Number(c.coa_id));
      queue.push(Number(c.coa_id));
    }
  }
  return ids;
}

function computeDepth(parentCoaId: number | null, allAccounts: CoaRow[]): number {
  if (parentCoaId == null) return 0;
  // eslint-disable-next-line eqeqeq
  const parent = allAccounts.find(a => a.coa_id == parentCoaId);
  return parent ? (Number(parent.coa_depth) || 0) + 1 : 0;
}

function buildBreadcrumb(account: CoaRow, allAccounts: CoaRow[]): string {
  const parts: string[] = [account.account_name];
  let current = account;
  let depth = 0;
  while (current.parent_coa_id != null && depth < 20) {
    // eslint-disable-next-line eqeqeq
    const parent = allAccounts.find(a => a.coa_id == current.parent_coa_id);
    if (!parent) break;
    parts.unshift(parent.account_name);
    current = parent;
    depth++;
  }
  return parts.join(' > ');
}

function suggestNextAccountNumber(parentCoaId: number | null, allAccounts: CoaRow[], selectedOwner: OwnerEntity | null): string {
  const siblings = allAccounts.filter(a =>
    // eslint-disable-next-line eqeqeq
    a.parent_coa_id == parentCoaId &&
    a.is_deleted !== 1 &&
    (selectedOwner ? a.owner_type === selectedOwner.owner_type && a.owner_id === selectedOwner.owner_id : true)
  );
  if (siblings.length === 0) {
    if (parentCoaId == null) return '1000';
    // eslint-disable-next-line eqeqeq
    const parent = allAccounts.find(a => a.coa_id == parentCoaId);
    return parent ? parent.account_number + '0' : '1000';
  }
  const numbers = siblings.map(s => parseInt(s.account_number, 10)).filter(n => !isNaN(n));
  if (numbers.length === 0) return '1000';
  const maxNum = Math.max(...numbers);
  return String(maxNum + 10);
}

function formatBalance(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function resolveCategoryName(cateId: number, categories: CategoryRow[], headers: HeaderRow[]): string {
  // eslint-disable-next-line eqeqeq
  const cat = categories.find(c => c.cate_id == cateId);
  if (!cat) return '';
  // eslint-disable-next-line eqeqeq
  const header = headers.find(h => h.header_id == cat.header_id);
  return header ? `${header.header_name} > ${cat.cate_name}` : cat.cate_name;
}

// =============================================
// Component
// =============================================
interface GLChartOfAccountsTabProps {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

const GLChartOfAccountsTab: React.FC<GLChartOfAccountsTabProps> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [currenciesData, setCurrenciesData] = useState<Record<string, any>[]>([]);
  const [allCoaData, setAllCoaData] = useState<CoaRow[]>([]);
  const [allCategoriesData, setAllCategoriesData] = useState<CategoryRow[]>([]);
  const [allSubCategoriesData, setAllSubCategoriesData] = useState<SubCategoryRow[]>([]);
  const [allHeadersData, setAllHeadersData] = useState<HeaderRow[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const [usersRows, bizRows, coaRows, cateRows, subCateRows, hdrRows, currRows] = await Promise.all([
          loadTable('users'),
          loadTable('user_businesses'),
          loadTable('acctg_coa'),
          loadTable('acctg_finsta_cate'),
          loadTable('acctg_finsta_cate_sub'),
          loadTable('acctg_finsta_headers'),
          loadTable('site_mstr_currencies'),
        ]);
        if (cancelled) return;
        setUsersData(usersRows);
        setBusinessesData(bizRows);
        setAllCoaData(coaRows as CoaRow[]);
        setAllCategoriesData(cateRows as CategoryRow[]);
        setAllSubCategoriesData(subCateRows as SubCategoryRow[]);
        setAllHeadersData(hdrRows as HeaderRow[]);
        setCurrenciesData(currRows);
        setDataLoaded(true);
      } catch {
        if (cancelled) return;
        setUsersData(getTableData('users'));
        setBusinessesData(getTableData('user_businesses'));
        setAllCoaData(getTableData('acctg_coa') as CoaRow[]);
        setAllCategoriesData(getTableData('acctg_finsta_cate') as CategoryRow[]);
        setAllSubCategoriesData(getTableData('acctg_finsta_cate_sub') as SubCategoryRow[]);
        setAllHeadersData(getTableData('acctg_finsta_headers') as HeaderRow[]);
        setCurrenciesData(getTableData('site_mstr_currencies'));
        setDataLoaded(true);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const currentUser = useMemo(() => dataLoaded ? getCurrentUser(usersData) : null, [usersData, dataLoaded]);
  const accountOwner = useMemo(() => {
    if (!currentUser) return null;
    return getAccountOwnerForUser(currentUser.user_id, usersData);
  }, [currentUser, usersData]);
  const accountOwnerId = accountOwner ? Number(accountOwner.user_id) : 0;
  const isAccountOwner = currentUser?.owner_id == null;

  const ownerEntities = useMemo(() => {
    if (!accountOwnerId) return [];
    const all = getOwnerEntities(accountOwnerId, usersData, businessesData);
    if (viewMode === 'clients') {
      if (!selectedClientId) return [];
      return all.filter(e => {
        // eslint-disable-next-line eqeqeq
        if (e.owner_type === 'client_personal' && e.owner_id == selectedClientId) return true;
        if (e.owner_type === 'client_personal' && e.badge.includes("'s Family")) {
          // eslint-disable-next-line eqeqeq
          const familyUser = usersData.find((u: any) => u.user_id == e.owner_id);
          // eslint-disable-next-line eqeqeq
          return familyUser && familyUser.owner_id == selectedClientId;
        }
        if (e.owner_type === 'client_business') {
          // eslint-disable-next-line eqeqeq
          const biz = businessesData.find((b: any) => b.business_id == e.owner_id);
          // eslint-disable-next-line eqeqeq
          return biz && biz.owner_id == selectedClientId;
        }
        return false;
      });
    }
    return all.filter(e => e.owner_type !== 'client_personal' && e.owner_type !== 'client_business');
  }, [accountOwnerId, usersData, businessesData, viewMode, selectedClientId]);

  const currencies = useMemo(() => parseCurrencies(currenciesData), [currenciesData]);

  // ---- Active accounts, categories & headers ----
  const [accounts, setAccounts] = useState<CoaRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryRow[]>([]);
  const [headers, setHeaders] = useState<HeaderRow[]>([]);

  useEffect(() => {
    if (dataLoaded && accountOwnerId) {
      setAccounts(filterActiveAccounts(allCoaData, accountOwnerId));
      setCategories(filterActiveCategories(allCategoriesData));
      setSubCategories(allSubCategoriesData.filter(sc => sc.is_deleted !== 1 && sc.is_active !== 0));
      setHeaders(allHeadersData.filter((h: any) =>
        h.is_active !== 0 && h.is_deleted !== 1
      ));
    }
  }, [dataLoaded, accountOwnerId, allCoaData, allCategoriesData, allSubCategoriesData, allHeadersData]);

  const refreshAccounts = useCallback(async () => {
    if (!accountOwnerId) return;
    try {
      const [coaRows, cateRows, subCateRows, hdrRows] = await Promise.all([
        loadTable('acctg_coa'),
        loadTable('acctg_finsta_cate'),
        loadTable('acctg_finsta_cate_sub'),
        loadTable('acctg_finsta_headers'),
      ]);
      setAllCoaData(coaRows as CoaRow[]);
      setAllCategoriesData(cateRows as CategoryRow[]);
      setAllSubCategoriesData(subCateRows as SubCategoryRow[]);
      setAllHeadersData(hdrRows as HeaderRow[]);
      setAccounts(filterActiveAccounts(coaRows as CoaRow[], accountOwnerId));
      setCategories(filterActiveCategories(cateRows as CategoryRow[]));
      setSubCategories((subCateRows as SubCategoryRow[]).filter(sc => sc.is_deleted !== 1 && sc.is_active !== 0));
      setHeaders((hdrRows as HeaderRow[]).filter((h: any) =>
        h.is_active !== 0 && h.is_deleted !== 1
      ));
    } catch {
      const coaRows = getTableData('acctg_coa') as CoaRow[];
      const cateRows = getTableData('acctg_finsta_cate') as CategoryRow[];
      const subCateRows = getTableData('acctg_finsta_cate_sub') as SubCategoryRow[];
      const hdrRows = getTableData('acctg_finsta_headers') as HeaderRow[];
      setAllCoaData(coaRows);
      setAllCategoriesData(cateRows);
      setAllSubCategoriesData(subCateRows);
      setAllHeadersData(hdrRows);
      setAccounts(filterActiveAccounts(coaRows, accountOwnerId));
      setCategories(filterActiveCategories(cateRows));
      setSubCategories(subCateRows.filter(sc => sc.is_deleted !== 1 && sc.is_active !== 0));
      setHeaders(hdrRows.filter((h: any) =>
        h.is_active !== 0 && h.is_deleted !== 1
      ));
    }
  }, [accountOwnerId]);

  // ---- State ----
  const [coaLevels, setCoaLevels] = useState<CoaLevel[]>([
    { inputText: '', selectedAccount: null, showAutocomplete: false },
  ]);
  const [mode, setMode] = useState<'idle' | 'delete'>('idle');
  const [editingAccount, setEditingAccount] = useState<CoaRow | null>(null);
  const [form, setForm] = useState<CoaFormFields>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formOwner, setFormOwner] = useState<OwnerEntity | null>(null);
  // A NEW account starts in its owner's base currency (Business setup / country). Only
  // while the field still holds the default — a currency the user picked is kept.
  const autoCoaCurrencyRef = useRef(EMPTY_FORM.currency);
  useEffect(() => {
    if (!formOwner || editingAccount) return;
    const base = entityBaseCurrency(formOwner.owner_type, formOwner.owner_id, usersData, businessesData);
    if (!base) return;
    const prevAuto = autoCoaCurrencyRef.current;
    autoCoaCurrencyRef.current = base;
    setForm(prev => (prev.currency === prevAuto || !prev.currency ? { ...prev, currency: base } : prev));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOwner, editingAccount, usersData, businessesData]);
  const [showSummary, setShowSummary] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<Record<number, number>>({});
  const [renamePrompt, setRenamePrompt] = useState<{ levelIndex: number; oldName: string; newName: string } | null>(null);
  const levelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const formCardRef = useRef<HTMLDivElement>(null);
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Close autocomplete on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (formCardRef.current && !formCardRef.current.contains(e.target as Node)) {
        setCoaLevels(prev => prev.map(l => ({ ...l, showAutocomplete: false })));
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Auto-focus the deepest empty sub-level input after account selection
  useEffect(() => {
    const lastIdx = coaLevels.length - 1;
    if (lastIdx > 0 && !coaLevels[lastIdx].selectedAccount && !coaLevels[lastIdx].inputText) {
      requestAnimationFrame(() => {
        const wrapper = levelRefs.current[lastIdx];
        const input = wrapper?.querySelector('input') as HTMLInputElement | null;
        input?.focus();
      });
    }
  }, [coaLevels.length]);

  // Auto-select owner for non-account-owners
  useEffect(() => {
    if (!isAccountOwner && currentUser && ownerEntities.length > 0 && !formOwner) {
      const myEntity = ownerEntities.find(
        e => (e.owner_type === 'family_member' || e.owner_type === 'client_personal') && e.owner_id === currentUser.user_id
      );
      if (myEntity) setFormOwner(myEntity);
    }
  }, [isAccountOwner, currentUser, ownerEntities, formOwner]);

  // ---- Derived state ----
  const isNewAtAnyLevel = useMemo(() => {
    return coaLevels.some(l => l.inputText.trim() && !l.selectedAccount);
  }, [coaLevels]);

  const deepestSelected = useMemo(() => {
    for (let i = coaLevels.length - 1; i >= 0; i--) {
      if (coaLevels[i].selectedAccount) return coaLevels[i].selectedAccount;
    }
    return null;
  }, [coaLevels]);

  const newLevelIndex = useMemo(() => {
    for (let i = 0; i < coaLevels.length; i++) {
      if (coaLevels[i].inputText.trim() && !coaLevels[i].selectedAccount) return i;
    }
    return -1;
  }, [coaLevels]);

  const isEditing = editingAccount !== null;

  const nameChanged = useMemo(() => {
    if (!editingAccount) return false;
    return form.account_name.trim() !== editingAccount.account_name.trim();
  }, [editingAccount, form.account_name]);

  const canReview = useMemo(() => {
    if (isNewAtAnyLevel) return true;
    if (isEditing && editingAccount) {
      const hasNameChange = form.account_name.trim() !== editingAccount.account_name.trim();
      const hasDescChange = form.account_description.trim() !== (editingAccount.account_description || '').trim()
        || (cleanList(form.alias_names) || '') !== (editingAccount.alias_names || '')
        || (cleanList(form.tags) || '') !== (editingAccount.tags || '');
      const hasCateChange = String(form.finsta_cate_id) !== String(editingAccount.finsta_cate_id || '');
      const hasSubCateChange = String(form.finsta_sub_cate_id) !== String(editingAccount.finsta_sub_cate_id || '');
      const hasNumChange = form.account_number.trim() !== (editingAccount.account_number || '').trim();
      const hasBalanceChange = form.normal_balance !== (editingAccount.normal_balance || '');
      const hasHeaderChange = form.is_header !== (editingAccount.is_header || 0);
      const hasOpenChange = String(parseFloat(form.opening_balance) || 0) !== String(editingAccount.opening_balance || 0);
      const hasOpenDateChange = (form.opening_balance_date || '') !== (editingAccount.opening_balance_date || '');
      const hasCurrChange = form.currency !== (editingAccount.currency || 'CAD');
      return hasNameChange || hasDescChange || hasCateChange || hasSubCateChange || hasNumChange || hasBalanceChange || hasHeaderChange || hasOpenChange || hasOpenDateChange || hasCurrChange;
    }
    return false;
  }, [isNewAtAnyLevel, isEditing, editingAccount, form]);

  const hasFormContent = isNewAtAnyLevel || isEditing || deepestSelected !== null;

  // Accounts available at each level
  const accountsForLevel = useMemo(() => {
    return coaLevels.map((level, idx) => {
      const parentId = idx === 0 ? null : coaLevels[idx - 1].selectedAccount?.coa_id ?? null;
      if (idx > 0 && parentId === null) return [];

      let candidates = accounts.filter(a => {
        if (idx === 0) return a.parent_coa_id == null;
        // eslint-disable-next-line eqeqeq
        return a.parent_coa_id == parentId;
      });

      // Filter by selected owner if one is chosen
      if (formOwner) {
        candidates = candidates.filter(a =>
          a.owner_type === formOwner.owner_type &&
          // eslint-disable-next-line eqeqeq
          a.owner_id == formOwner.owner_id
        );
      }

      if (!level.inputText.trim()) return candidates.sort((a, b) => a.account_number.localeCompare(b.account_number));
      // Found by name, number, an alias or a tag. Those STARTING with what was typed
      // come first, then a word / alias / tag starting with it, then the rest.
      const lower = level.inputText.trim().toLowerCase();
      const words = (v: unknown) => ` ${String(v || '').toLowerCase().replace(/[,\s]+/g, ' ')}`;
      const rank = (a: CoaRow): number => {
        const name = (a.account_name || '').toLowerCase();
        const num = String(a.account_number || '').toLowerCase();
        if (name.startsWith(lower) || num.startsWith(lower)) return 0;
        if (words(name).includes(` ${lower}`) || words(a.alias_names).includes(` ${lower}`) || words(a.tags).includes(` ${lower}`)) return 1;
        if (name.includes(lower) || num.includes(lower)
          || String(a.alias_names || '').toLowerCase().includes(lower) || String(a.tags || '').toLowerCase().includes(lower)) return 2;
        return -1;
      };
      return candidates
        .map(a => ({ a, r: rank(a) }))
        .filter(x => x.r >= 0)
        .sort((x, y) => x.r - y.r || String(x.a.account_number || '').localeCompare(String(y.a.account_number || '')))
        .map(x => x.a);
    });
  }, [coaLevels, accounts, formOwner]);

  const breadcrumbPath = useMemo(() => {
    return coaLevels
      .filter(l => l.selectedAccount || l.inputText.trim())
      .map(l => l.selectedAccount
        ? `${l.selectedAccount.account_number} ${l.selectedAccount.account_name}`
        : l.inputText.trim())
      .join(' > ');
  }, [coaLevels]);

  // ---- Categories grouped by header for dropdown ----
  const sortedHeaders = useMemo(() =>
    [...headers].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
  [headers]);

  const sortedCategories = useMemo(() => {
    const result: CategoryRow[] = [];
    for (const hdr of sortedHeaders) {
      const children = categories
        // eslint-disable-next-line eqeqeq
        .filter(c => c.header_id == hdr.header_id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      result.push(...children);
    }
    return result;
  }, [categories, sortedHeaders]);

  const filteredSubCategories = useMemo(() => {
    let subs = subCategories;
    if (form.finsta_cate_id) {
      // eslint-disable-next-line eqeqeq
      subs = subs.filter(sc => sc.cate_id == Number(form.finsta_cate_id));
    }
    return subs.sort((a, b) => {
      if (a.cate_id !== b.cate_id) return a.cate_id - b.cate_id;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [subCategories, form.finsta_cate_id]);

  // ---- Level handlers ----
  const handleLevelInput = useCallback((levelIndex: number, text: string) => {
    // Emptying the first box puts the form back to blank: nothing is left to save.
    setFormDirty(!(levelIndex === 0 && !text.trim()));
    setCoaLevels(prev => {
      const next = prev.slice(0, levelIndex + 1);
      next[levelIndex] = {
        inputText: text,
        selectedAccount: null,
        showAutocomplete: true,
      };
      return next;
    });
    setHighlightedIndex(prev => ({ ...prev, [levelIndex]: -1 }));
    setFormErrors({});
    setShowSummary(false);
    if (levelIndex === 0 && !text.trim()) {
      setForm(EMPTY_FORM);
      setFormOwner(null);
      setEditingAccount(null);
    }
  }, []);

  const handleSelectAccount = useCallback((levelIndex: number, account: CoaRow) => {
    setCoaLevels(prev => {
      const next = prev.slice(0, levelIndex + 1);
      next[levelIndex] = {
        inputText: account.account_name,
        selectedAccount: account,
        showAutocomplete: false,
      };
      if (next.length <= MAX_COA_LEVELS) {
        next.push({ inputText: '', selectedAccount: null, showAutocomplete: false });
      }
      return next;
    });

    setForm({
      finsta_cate_id: account.finsta_cate_id ? String(account.finsta_cate_id) : '',
      finsta_sub_cate_id: account.finsta_sub_cate_id ? String(account.finsta_sub_cate_id) : '',
      account_number: account.account_number || '',
      account_name: account.account_name || '',
      account_description: account.account_description || '',
      alias_names: account.alias_names || '',
      tags: account.tags || '',
      normal_balance: account.normal_balance || '',
      is_header: account.is_header || 0,
      parent_coa_id: account.parent_coa_id != null ? String(account.parent_coa_id) : '',
      opening_balance: String(account.opening_balance ?? '0.00'),
      opening_balance_date: account.opening_balance_date || '',
      currency: account.currency || 'CAD',
    });

    const ownerEntity = ownerEntities.find(e =>
      e.owner_type === account.owner_type &&
      // eslint-disable-next-line eqeqeq
      e.owner_id == Number(account.owner_id)
    ) || null;
    setFormOwner(ownerEntity);
    setEditingAccount(account);
    setFormErrors({});
    setShowSummary(false);
    setRenamePrompt(null);
  }, [ownerEntities]);

  const handleClearLevel = useCallback((levelIndex: number) => {
    setCoaLevels(prev => {
      if (levelIndex === 0) {
        return [{ inputText: '', selectedAccount: null, showAutocomplete: false }];
      }
      return prev.slice(0, levelIndex).concat([{ inputText: '', selectedAccount: null, showAutocomplete: false }]);
    });
    if (levelIndex === 0) {
      setFormDirty(false); // the whole form is blank again
      setForm(EMPTY_FORM);
      setFormOwner(null);
      setEditingAccount(null);
    } else {
      const parentLevel = coaLevels[levelIndex - 1];
      if (parentLevel?.selectedAccount) {
        const account = parentLevel.selectedAccount;
        setForm({
          finsta_cate_id: account.finsta_cate_id ? String(account.finsta_cate_id) : '',
          finsta_sub_cate_id: account.finsta_sub_cate_id ? String(account.finsta_sub_cate_id) : '',
          account_number: account.account_number || '',
          account_name: account.account_name || '',
          account_description: account.account_description || '',
          alias_names: account.alias_names || '',
          tags: account.tags || '',
          normal_balance: account.normal_balance || '',
          is_header: account.is_header || 0,
          parent_coa_id: account.parent_coa_id != null ? String(account.parent_coa_id) : '',
          opening_balance: String(account.opening_balance ?? '0.00'),
          opening_balance_date: account.opening_balance_date || '',
          currency: account.currency || 'CAD',
        });
        const ownerEntity = ownerEntities.find(e =>
          e.owner_type === account.owner_type &&
          // eslint-disable-next-line eqeqeq
          e.owner_id == Number(account.owner_id)
        ) || null;
        setFormOwner(ownerEntity);
        setEditingAccount(account);
      }
    }
    setFormErrors({});
    setShowSummary(false);
    setRenamePrompt(null);
  }, [coaLevels, ownerEntities]);

  const handleLevelKeyDown = useCallback((levelIndex: number, e: React.KeyboardEvent, levelAccounts: CoaRow[]) => {
    const currentIdx = highlightedIndex[levelIndex] ?? -1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(currentIdx + 1, levelAccounts.length - 1);
      setHighlightedIndex(prev => ({ ...prev, [levelIndex]: next }));
      const item = dropdownRefs.current[levelIndex]?.children[next] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(currentIdx - 1, 0);
      setHighlightedIndex(prev => ({ ...prev, [levelIndex]: next }));
      const item = dropdownRefs.current[levelIndex]?.children[next] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (currentIdx >= 0 && currentIdx < levelAccounts.length) {
        handleSelectAccount(levelIndex, levelAccounts[currentIdx]);
        setHighlightedIndex(prev => ({ ...prev, [levelIndex]: -1 }));
      }
    } else if (e.key === 'Escape') {
      setCoaLevels(prev => {
        const next = [...prev];
        next[levelIndex] = { ...next[levelIndex], showAutocomplete: false };
        return next;
      });
      setHighlightedIndex(prev => ({ ...prev, [levelIndex]: -1 }));
    }
  }, [highlightedIndex, handleSelectAccount]);

  const closeForm = useCallback(() => {
    setFormDirty(false);
    setCoaLevels([{ inputText: '', selectedAccount: null, showAutocomplete: false }]);
    setMode('idle');
    setEditingAccount(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setFormOwner(null);
    setShowSummary(false);
    setRenamePrompt(null);
  }, []);

  // ---- Form handlers ----
  const handleFormChange = useCallback((field: keyof CoaFormFields, value: string | number) => {
    setFormDirty(true);
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
    setShowSummary(false);
  }, []);

  const handleCategoryChange = useCallback((cateIdStr: string) => {
    handleFormChange('finsta_cate_id', cateIdStr);
    // Clear sub-category when category changes
    setForm(prev => ({ ...prev, finsta_cate_id: cateIdStr, finsta_sub_cate_id: '' }));
    if (cateIdStr) {
      // eslint-disable-next-line eqeqeq
      const cat = categories.find(c => c.cate_id == Number(cateIdStr));
      if (cat) {
        // eslint-disable-next-line eqeqeq
        const hdr = headers.find(h => h.header_id == cat.header_id);
        const suggestedBalance = hdr ? NORMAL_BALANCE_BY_HEADER[hdr.header_name] : undefined;
        if (suggestedBalance) {
          setForm(prev => ({ ...prev, finsta_cate_id: cateIdStr, finsta_sub_cate_id: '', normal_balance: suggestedBalance }));
        }
      }
    }
  }, [categories, headers, handleFormChange]);

  const handleSubCategoryChange = useCallback((subCateIdStr: string) => {
    handleFormChange('finsta_sub_cate_id', subCateIdStr);
    if (subCateIdStr) {
      // eslint-disable-next-line eqeqeq
      const subCat = subCategories.find(sc => sc.sub_cate_id == Number(subCateIdStr));
      if (subCat) {
        // Auto-set category from sub-category's parent
        const cateIdStr = String(subCat.cate_id);
        // eslint-disable-next-line eqeqeq
        const cat = categories.find(c => c.cate_id == subCat.cate_id);
        if (cat) {
          // eslint-disable-next-line eqeqeq
          const hdr = headers.find(h => h.header_id == cat.header_id);
          const suggestedBalance = hdr ? NORMAL_BALANCE_BY_HEADER[hdr.header_name] : undefined;
          setForm(prev => ({
            ...prev,
            finsta_sub_cate_id: subCateIdStr,
            finsta_cate_id: cateIdStr,
            ...(suggestedBalance ? { normal_balance: suggestedBalance } : {}),
          }));
        }
      }
    }
  }, [subCategories, categories, headers, handleFormChange]);

  const openDeleteConfirm = useCallback((account: CoaRow) => {
    setEditingAccount(account);
    setMode('delete');
  }, []);

  // ---- Validate ----
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    const accountName = isNewAtAnyLevel && newLevelIndex >= 0
      ? coaLevels[newLevelIndex].inputText.trim()
      : form.account_name.trim();

    if (!accountName) {
      errors.account_name = 'Account name is required';
    }

    if (form.account_number.trim() && formOwner) {
      const dup = accounts.find(a =>
        a.account_number === form.account_number.trim() &&
        a.owner_type === formOwner.owner_type &&
        // eslint-disable-next-line eqeqeq
        a.owner_id == formOwner.owner_id &&
        a.is_deleted !== 1 &&
        (!editingAccount || Number(a.coa_id) !== Number(editingAccount.coa_id))
      );
      if (dup) {
        errors.account_number = `Account number "${form.account_number.trim()}" already exists for this owner`;
      }
    }

    if (accountName) {
      const parentId = isNewAtAnyLevel && newLevelIndex >= 0
        ? (newLevelIndex > 0 ? Number(coaLevels[newLevelIndex - 1].selectedAccount?.coa_id) : null)
        : (form.parent_coa_id ? Number(form.parent_coa_id) : null);

      const dupFilter = (a: CoaRow) => {
        if (a.account_name.toLowerCase() !== accountName.toLowerCase()) return false;
        // eslint-disable-next-line eqeqeq
        if (a.parent_coa_id != parentId) return false;
        if (a.is_deleted === 1) return false;
        if (editingAccount && Number(a.coa_id) === Number(editingAccount.coa_id)) return false;
        if (formOwner) {
          // eslint-disable-next-line eqeqeq
          return a.owner_type === formOwner.owner_type && a.owner_id == formOwner.owner_id;
        }
        return true;
      };

      const dup = accounts.find(dupFilter);
      if (dup) {
        errors.account_name = 'An account with this name already exists at this level';
      }
    }

    if (form.opening_balance && isNaN(parseFloat(form.opening_balance))) {
      errors.opening_balance = 'Must be a valid number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form, accounts, editingAccount, formOwner, coaLevels, isNewAtAnyLevel, newLevelIndex]);

  const handleReview = useCallback(() => {
    if (validateForm()) setShowSummary(true);
  }, [validateForm]);

  // ---- Save ----
  const handleSave = useCallback(async () => {
    if (!validateForm() || !accountOwnerId || !currentUser) return;

    const now = NOW_ISO();

    if (isNewAtAnyLevel && newLevelIndex >= 0) {
      const accountName = coaLevels[newLevelIndex].inputText.trim();
      const parentAccount = newLevelIndex > 0 ? coaLevels[newLevelIndex - 1].selectedAccount : null;
      const parentId = parentAccount ? Number(parentAccount.coa_id) : null;
      const depth = computeDepth(parentId, accounts);

      // Default category: Current Assets (Debit) or Current Liabilities (Credit) if not selected
      let resolvedCateId = form.finsta_cate_id ? Number(form.finsta_cate_id) : 0;
      if (!resolvedCateId) {
        const balance = form.normal_balance || 'Debit';
        const defaultCatName = balance === 'Credit' ? 'Other Current Liabilities' : 'Current Assets';
        const defaultCat = categories.find(c => c.cate_name === defaultCatName);
        if (defaultCat) resolvedCateId = defaultCat.cate_id;
      }

      const resolvedBalance = form.normal_balance || 'Debit';

      const newAccountData: Record<string, any> = {
        account_owner_id: accountOwnerId,
        owner_type: formOwner ? formOwner.owner_type : 'account_owner',
        owner_id: formOwner ? formOwner.owner_id : accountOwnerId,
        parent_coa_id: parentId,
        coa_depth: depth,
        finsta_cate_id: resolvedCateId || null,
        finsta_sub_cate_id: form.finsta_sub_cate_id ? Number(form.finsta_sub_cate_id) : null,
        account_number: form.account_number.trim() || null,
        account_name: accountName,
        account_description: form.account_description.trim(),
        alias_names: cleanList(form.alias_names),
        tags: cleanList(form.tags),
        normal_balance: resolvedBalance,
        is_header: form.is_header,
        opening_balance: parseFloat(form.opening_balance) || 0,
        opening_balance_date: form.opening_balance_date || null,
        current_balance: parseFloat(form.opening_balance) || 0,
        currency: form.currency || 'CAD',
        sort_order: 0,
        is_active: 1,
        is_deleted: 0,
        created_by: currentUser.user_id,
        created_at: now,
        updated_at: now,
      };

      try {
        await addRow('acctg_coa', newAccountData, 'coa_id');
        recordTagUse(form.tags);
      } catch { /* handled internally */ }

      await refreshAccounts();
      setSavedMsg('Account created!');
      setTimeout(() => setSavedMsg(''), 3000);

      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'coa_created',
        title: 'GL Account created',
        message: `${userName} created account "${form.account_number.trim()} ${accountName}".`,
        actor_user_id: currentUser.user_id,
        actor_name: userName,
      });

      closeForm();

    } else if (isEditing && editingAccount) {
      const parentId = form.parent_coa_id ? Number(form.parent_coa_id) : null;

      let resolvedCateId = form.finsta_cate_id ? Number(form.finsta_cate_id) : 0;
      if (!resolvedCateId) {
        const balance = form.normal_balance || 'Debit';
        const defaultCatName = balance === 'Credit' ? 'Other Current Liabilities' : 'Current Assets';
        const defaultCat = categories.find(c => c.cate_name === defaultCatName);
        if (defaultCat) resolvedCateId = defaultCat.cate_id;
      }

      const updates: Record<string, any> = {
        finsta_cate_id: resolvedCateId || null,
        finsta_sub_cate_id: form.finsta_sub_cate_id ? Number(form.finsta_sub_cate_id) : null,
        account_number: form.account_number.trim() || null,
        account_name: form.account_name.trim(),
        account_description: form.account_description.trim(),
        alias_names: cleanList(form.alias_names),
        tags: cleanList(form.tags),
        normal_balance: form.normal_balance || 'Debit',
        is_header: form.is_header,
        parent_coa_id: parentId,
        coa_depth: computeDepth(parentId, accounts),
        opening_balance: parseFloat(form.opening_balance) || 0,
        opening_balance_date: form.opening_balance_date || null,
        currency: form.currency || 'CAD',
        owner_type: formOwner ? formOwner.owner_type : 'account_owner',
        owner_id: formOwner ? formOwner.owner_id : accountOwnerId,
        updated_at: now,
      };

      try {
        await saveRow('acctg_coa', editingAccount.coa_id, updates, 'coa_id');
        recordTagUse(form.tags, editingAccount.tags);
      } catch { /* handled internally */ }

      await refreshAccounts();
      setSavedMsg('Account updated!');
      setTimeout(() => setSavedMsg(''), 3000);

      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'coa_edited',
        title: 'GL Account edited',
        message: `${userName} edited account "${form.account_number.trim()} ${form.account_name.trim()}".`,
        actor_user_id: currentUser.user_id,
        actor_name: userName,
      });

      closeForm();
    }
  }, [isNewAtAnyLevel, newLevelIndex, isEditing, editingAccount, coaLevels, form, formOwner, accountOwnerId, currentUser, accounts, categories, refreshAccounts, closeForm, validateForm]);

  // "Save & continue" for the GL account form (modal).
  useEffect(() => {
    registerSaveHandler(submitSaver(handleSave));
    return () => registerSaveHandler(null);
  }, [handleSave]);

  // A flag left set when this tab goes away would follow the user to the next screen.
  useEffect(() => () => setFormDirty(false), []);

  // ---- Delete ----
  const handleDelete = useCallback(async () => {
    if (!editingAccount || !currentUser || !accountOwnerId) return;

    const coaId = Number(editingAccount.coa_id);
    const idsToDelete = [coaId, ...getDescendantIds(coaId, accounts)];

    for (const id of idsToDelete) {
      try {
        await removeRow('acctg_coa', id, 'coa_id');
      } catch { /* handled internally */ }
    }

    const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
    addNotification({
      account_owner_id: accountOwnerId,
      type: 'coa_deleted',
      title: 'GL Account deleted',
      message: `${userName} deleted account "${editingAccount.account_number} ${editingAccount.account_name}"${idsToDelete.length > 1 ? ` and ${idsToDelete.length - 1} sub-account(s)` : ''}.`,
      actor_user_id: currentUser.user_id,
      actor_name: userName,
    });

    await refreshAccounts();
    setSavedMsg('Account deleted.');
    setTimeout(() => setSavedMsg(''), 3000);
    closeForm();
  }, [editingAccount, currentUser, accountOwnerId, accounts, refreshAccounts, closeForm]);

  // ---- Delete info ----
  const deleteChildCount = useMemo(() => {
    if (!editingAccount) return 0;
    return getDescendantIds(Number(editingAccount.coa_id), accounts).length;
  }, [editingAccount, accounts]);

  // ---- Loading / No user ----
  if (!currentUser || !accountOwner) {
    if (!dataLoaded) {
      return (
        <div className="coa-tab">
          <div className="coa-empty">
            <div className="coa-empty-icon"><FiBookOpen size={48} /></div>
            <p>Loading accounts...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="coa-tab">
        <div className="coa-empty">
          <div className="coa-empty-icon"><FiBookOpen size={48} /></div>
          <p>Please log in to manage the Chart of Accounts.</p>
        </div>
      </div>
    );
  }

  // ---- Render ----
  return (
    <div className="coa-tab">
      <div className="coa-info-banner">
        Set up your General Ledger Chart of Accounts. Type an account name to search existing or create new.<br />
        Select an existing account to edit or add sub-accounts below it.
      </div>

      {savedMsg && (
        <div className="coa-action-bar">
          <span className="coa-saved-msg">{savedMsg}</span>
        </div>
      )}

      {/* ---- Always-visible Form Card ---- */}
      <div className="coa-form-card" ref={formCardRef}>

        {/* Cascading level inputs */}
        {coaLevels.map((level, idx) => {
          const levelAccounts = accountsForLevel[idx] || [];
          const isRoot = idx === 0;
          const parentAccount = idx > 0 ? coaLevels[idx - 1].selectedAccount : null;

          if (idx > 0 && !coaLevels[idx - 1].selectedAccount) return null;

          return (
            <div
              key={idx}
              className="coa-field-group coa-level-wrapper"
              ref={el => { levelRefs.current[idx] = el; }}
              style={idx > 0 ? { marginLeft: idx * 16, borderLeft: '2px solid var(--gray-200)', paddingLeft: 12 } : undefined}
            >
              <label className="coa-field-label">
                {COA_LEVEL_LABELS[idx] || `Level ${idx + 1}`}
                {isRoot && <span className="coa-required">*</span>}
                {idx > 0 && parentAccount && (
                  <span className="coa-field-hint" style={{ marginLeft: 8, display: 'inline' }}>
                    in &ldquo;{parentAccount.account_number} {parentAccount.account_name}&rdquo;
                  </span>
                )}
              </label>

              <div className="coa-search-wrapper">
                <FiSearch className="coa-search-icon" size={16} />
                <input
                  className={`coa-search-input ${idx === newLevelIndex && formErrors.account_name ? 'coa-input-error' : ''}`}
                  type="text"
                  placeholder={isRoot ? 'Type an account name to search or create...' : 'Type a sub-account name...'}
                  value={level.inputText}
                  readOnly={!!(level.selectedAccount && level.selectedAccount.coa_id !== editingAccount?.coa_id)}
                  style={level.selectedAccount && level.selectedAccount.coa_id !== editingAccount?.coa_id ? { background: '#f5f5f5', color: '#666' } : undefined}
                  onChange={e => {
                    if (level.selectedAccount && level.selectedAccount.coa_id === editingAccount?.coa_id) {
                      const val = e.target.value;
                      setCoaLevels(prev => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], inputText: val };
                        return next;
                      });
                      handleFormChange('account_name', val);
                    } else if (!level.selectedAccount) {
                      handleLevelInput(idx, e.target.value);
                    }
                  }}
                  onKeyDown={e => {
                    if (level.showAutocomplete && !level.selectedAccount) {
                      handleLevelKeyDown(idx, e, levelAccounts);
                    }
                  }}
                  onFocus={() => {
                    if (!level.selectedAccount) {
                      setCoaLevels(prev => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], showAutocomplete: true };
                        return next;
                      });
                    }
                  }}
                  onBlur={() => {
                    if (level.selectedAccount && level.selectedAccount.coa_id === editingAccount?.coa_id) {
                      const newName = level.inputText.trim();
                      const oldName = level.selectedAccount.account_name.trim();
                      if (newName && newName !== oldName) {
                        setRenamePrompt({ levelIndex: idx, oldName, newName });
                      }
                    }
                  }}
                />
                {/* nothing in the box = nothing to clear */}
                {level.inputText && (
                  <button className="coa-search-clear" onClick={() => handleClearLevel(idx)}>
                    <FiX size={16} />
                  </button>
                )}
              </div>

              {/* Autocomplete dropdown */}
              {level.showAutocomplete && !level.selectedAccount && (
                <div className="coa-dropdown" ref={el => { dropdownRefs.current[idx] = el; }}>
                  {levelAccounts.map((account, ai) => {
                    const ownerBadge = ownerEntities.find(e =>
                      e.owner_type === account.owner_type &&
                      // eslint-disable-next-line eqeqeq
                      e.owner_id == Number(account.owner_id)
                    )?.badge || '';
                    return (
                      <div
                        key={account.coa_id}
                        className={`coa-dropdown-item ${(highlightedIndex[idx] ?? -1) === ai ? 'highlighted' : ''}`}
                        onClick={() => handleSelectAccount(idx, account)}
                      >
                        <span className="coa-dropdown-num">{account.account_number}</span>
                        <span>{account.account_name}</span>
                        {!formOwner && ownerBadge && (
                          <span className="coa-dropdown-badge">{ownerBadge}</span>
                        )}
                        {isAccountOwner && (
                          <button
                            className="coa-btn-icon danger"
                            onClick={e => { e.stopPropagation(); openDeleteConfirm(account); }}
                            title="Delete"
                            style={{ marginLeft: 'auto', width: 28, height: 28 }}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {level.inputText.trim() && !levelAccounts.some(a => a.account_name.toLowerCase() === level.inputText.trim().toLowerCase()) && (
                    <div
                      className="coa-dropdown-item coa-create-option"
                      onClick={() => {
                        setCoaLevels(prev => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], showAutocomplete: false };
                          return next;
                        });
                      }}
                    >
                      <FiPlus size={16} />
                      + New {isRoot ? 'account' : 'sub-account'}: &ldquo;{level.inputText.trim()}&rdquo;
                    </div>
                  )}
                  {!level.inputText.trim() && levelAccounts.length === 0 && (
                    <div className="coa-dropdown-item" style={{ color: 'var(--gray-400)', cursor: 'default' }}>
                      No {isRoot ? 'accounts' : 'sub-accounts'} yet. Type a name to create one.
                    </div>
                  )}
                </div>
              )}

              {idx === newLevelIndex && formErrors.account_name && (
                <span className="coa-field-error">{formErrors.account_name}</span>
              )}

              {/* Rename prompt */}
              {renamePrompt && renamePrompt.levelIndex === idx && (
                <div className="coa-rename-prompt">
                  <div className="coa-rename-prompt-text">
                    <FiEdit2 size={14} />
                    <span>You changed &ldquo;{renamePrompt.oldName}&rdquo; to &ldquo;{renamePrompt.newName}&rdquo;</span>
                  </div>
                  <div className="coa-rename-prompt-actions">
                    <button
                      className="coa-btn-primary"
                      style={{ fontSize: 13, padding: '5px 14px' }}
                      onClick={() => {
                        handleFormChange('account_name', renamePrompt.newName);
                        setRenamePrompt(null);
                      }}
                    >
                      <FiEdit2 size={13} /> Rename &ldquo;{renamePrompt.oldName}&rdquo;
                    </button>
                    <button
                      className="coa-btn-secondary"
                      style={{ fontSize: 13, padding: '5px 14px' }}
                      onClick={() => {
                        setCoaLevels(prev => {
                          const next = [...prev];
                          next[idx] = { inputText: renamePrompt.newName, selectedAccount: null, showAutocomplete: false };
                          return next.slice(0, idx + 1);
                        });
                        setEditingAccount(idx > 0 ? coaLevels[idx - 1]?.selectedAccount || null : null);
                        setForm(prev => ({ ...prev, account_name: '' }));
                        setRenamePrompt(null);
                      }}
                    >
                      <FiPlus size={13} /> Add New &ldquo;{renamePrompt.newName}&rdquo;
                    </button>
                    <button
                      className="coa-btn-secondary"
                      style={{ fontSize: 13, padding: '5px 14px' }}
                      onClick={() => {
                        setCoaLevels(prev => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], inputText: renamePrompt.oldName };
                          return next;
                        });
                        handleFormChange('account_name', renamePrompt.oldName);
                        setRenamePrompt(null);
                      }}
                    >
                      <FiX size={13} /> Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Breadcrumb path */}
        {breadcrumbPath && coaLevels.some(l => l.selectedAccount || l.inputText.trim()) && (
          <div className="coa-level-breadcrumb">
            <FiChevronRight size={12} style={{ flexShrink: 0 }} />
            <span>{breadcrumbPath}</span>
          </div>
        )}

        {/* ---- Owner Chips (optional single select) ---- */}
        <div className="coa-field-group" style={{ marginTop: 16 }}>
          <label className="coa-field-label">
            Owner
            {isAccountOwner && <span className="coa-field-hint" style={{ display: 'inline', marginLeft: 8 }}>(select one owner, or leave blank to show for all)</span>}
          </label>
          <div className="coa-owner-grid" style={{ marginTop: 4 }}>
            {ownerEntities.map(entity => {
              const isSelected = formOwner?.owner_type === entity.owner_type && formOwner?.owner_id === entity.owner_id;
              const disabled = !isAccountOwner && entity.owner_type !== 'family_member' && entity.owner_id !== currentUser.user_id;
              return (
                <button
                  key={`${entity.owner_type}-${entity.owner_id}`}
                  className={`coa-owner-chip ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    if (disabled) return;
                    setFormOwner(isSelected ? null : entity);
                    setFormErrors(prev => ({ ...prev, owner: '' }));
                    setShowSummary(false);
                  }}
                  title={disabled ? 'You can only manage your own accounts' : entity.display_name}
                  style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                >
                  {entity.display_name}
                  <span className="coa-chip-badge">{entity.badge}</span>
                </button>
              );
            })}
          </div>
          {ownerEntities.length === 0 && (
            <p className="coa-field-hint">No owner entities found. Please set up your profile first.</p>
          )}
          {!formOwner && ownerEntities.length > 0 && (
            <span className="coa-field-hint">No owner selected — this account will be visible to all family members and businesses</span>
          )}
        </div>

        {/* ---- Category ---- */}
        <div className="coa-field-group">
          <label className="coa-field-label">Financial Statement Category</label>
          <SearchableDropdown
            className="coa-select"
            options={sortedCategories.map(c => {
              // eslint-disable-next-line eqeqeq
              const hdr = headers.find(h => h.header_id == c.header_id);
              return {
                value: String(c.cate_id),
                label: c.cate_name,
                group: hdr ? hdr.header_name.toUpperCase() : '',
              };
            })}
            value={form.finsta_cate_id}
            onChange={handleCategoryChange}
            placeholder="Select a category..."
          />
          <span className="coa-field-hint">Determines which financial statement this account appears on</span>
        </div>

        {/* ---- Sub-Category (Detail Type) ---- */}
        <div className="coa-field-group">
          <label className="coa-field-label">Sub-Category (Detail Type)</label>
          <SearchableDropdown
            className="coa-select"
            options={filteredSubCategories.map(sc => {
              // eslint-disable-next-line eqeqeq
              const cat = categories.find(c => c.cate_id == sc.cate_id);
              // eslint-disable-next-line eqeqeq
              const hdr = cat ? headers.find(h => h.header_id == cat.header_id) : null;
              const groupLabel = hdr && cat ? `${hdr.header_name} > ${cat.cate_name}` : (cat ? cat.cate_name : '');
              return {
                value: String(sc.sub_cate_id),
                label: sc.sub_cate_name,
                group: form.finsta_cate_id ? '' : groupLabel.toUpperCase(),
              };
            })}
            value={form.finsta_sub_cate_id}
            onChange={handleSubCategoryChange}
            placeholder={form.finsta_cate_id ? 'Select a detail type...' : 'Select a category first, or pick any detail type...'}
          />
          <span className="coa-field-hint">QuickBooks detail type — auto-sets the category above when selected</span>
        </div>

        {/* ---- Account Number ---- */}
        <div className="coa-field-group">
          <label className="coa-field-label">Account Number</label>
          <input
            className={`coa-input ${formErrors.account_number ? 'coa-input-error' : ''}`}
            type="text"
            value={form.account_number}
            onChange={e => handleFormChange('account_number', e.target.value)}
            placeholder="e.g. 1000"
            style={{ fontFamily: 'monospace' }}
          />
          {formErrors.account_number && <span className="coa-field-error">{formErrors.account_number}</span>}
        </div>

        {/* ---- Description ---- */}
        <div className="coa-field-group">
          <label className="coa-field-label">Description</label>
          <textarea
            className="coa-input coa-textarea"
            value={form.account_description}
            onChange={e => handleFormChange('account_description', e.target.value)}
            placeholder="Optional description..."
          />
        </div>

        {/* ---- Alias names + Tags: other ways to find this account ---- */}
        <div className="coa-field-row">
          <div className="coa-field-group">
            <label className="coa-field-label">Alias names</label>
            <input
              className="coa-input"
              type="text"
              value={form.alias_names}
              onChange={e => handleFormChange('alias_names', e.target.value)}
              placeholder="Other names, separated by commas — e.g. hydro, power bill"
            />
          </div>
          <div className="coa-field-group">
            <label className="coa-field-label" htmlFor="coa-form-tags">Tags</label>
            <TagInput
              id="coa-form-tags"
              value={form.tags}
              onChange={v => handleFormChange('tags', v)}
              placeholder="Type a tag — e.g. rental, 118 1SNW"
            />
          </div>
        </div>

        {/* ---- Normal Balance + Account Type ---- */}
        <div className="coa-field-row">
          <div className="coa-field-group">
            <label className="coa-field-label">Normal Balance</label>
            <SearchableDropdown
              className="coa-select"
              options={[
                { value: 'Debit', label: 'Debit (Assets, Expenses, COGS)' },
                { value: 'Credit', label: 'Credit (Liabilities, Equity, Income)' },
              ]}
              value={form.normal_balance}
              onChange={(val) => handleFormChange('normal_balance', val)}
              placeholder="Select balance type..."
            />
            <span className="coa-field-hint">Auto-set when you pick a category</span>
          </div>
          <div className="coa-field-group">
            <label className="coa-field-label">Account Type</label>
            <div className="coa-toggle-row">
              <label className="coa-toggle">
                <input
                  type="checkbox"
                  checked={form.is_header === 1}
                  onChange={e => handleFormChange('is_header', e.target.checked ? 1 : 0)}
                />
                <span className="coa-toggle-slider" />
              </label>
              <span className="coa-toggle-label">
                {form.is_header ? 'Header account (grouping only — no transactions)' : 'Detail account (transactions post here)'}
              </span>
            </div>
          </div>
        </div>

        {/* ---- Opening Balance + Date + Currency (hidden for header accounts) ---- */}
        {form.is_header !== 1 && (
          <>
            <div className="coa-field-row">
              <div className="coa-field-group">
                <label className="coa-field-label">Opening Balance</label>
                <input
                  className={`coa-input ${formErrors.opening_balance ? 'coa-input-error' : ''}`}
                  type="text"
                  value={form.opening_balance}
                  onChange={e => handleFormChange('opening_balance', e.target.value)}
                  placeholder="0.00"
                  style={{ fontFamily: 'monospace' }}
                />
                {formErrors.opening_balance && <span className="coa-field-error">{formErrors.opening_balance}</span>}
              </div>
              <div className="coa-field-group">
                <label className="coa-field-label">Opening Balance Date</label>
                <input
                  className="coa-input"
                  type="date"
                  value={form.opening_balance_date}
                  onChange={e => handleFormChange('opening_balance_date', e.target.value)}
                />
              </div>
            </div>
            <div className="coa-field-group">
              <label className="coa-field-label">Currency</label>
              <SearchableDropdown
                className="coa-select"
                options={currencies.length > 0
                  ? currencies.map(c => ({
                      value: c.code,
                      label: `${c.code} — ${c.name} ${c.symbol ? `(${c.symbol})` : ''}`,
                    }))
                  : [
                      { value: 'CAD', label: 'CAD — Canadian Dollar' },
                      { value: 'USD', label: 'USD — US Dollar' },
                      { value: 'GBP', label: 'GBP — British Pound' },
                      { value: 'EUR', label: 'EUR — Euro' },
                      { value: 'INR', label: 'INR — Indian Rupee' },
                    ]
                }
                value={form.currency}
                onChange={(val) => handleFormChange('currency', val)}
                placeholder="Select currency..."
              />
            </div>
          </>
        )}

        {/* ---- Summary ---- */}
        {showSummary && (
          <div className="coa-summary">
            <div className="coa-summary-title"><FiCheck size={16} /> Review Before Saving</div>
            <div className="coa-summary-row">
              <span className="coa-summary-label">Action</span>
              <span className="coa-summary-value">
                {isNewAtAnyLevel ? 'Create New Account' : nameChanged ? `Rename "${editingAccount?.account_name}" → "${form.account_name.trim()}"` : 'Edit Account'}
              </span>
            </div>
            {/* Show hierarchy by level */}
            {coaLevels.map((level, idx) => {
              if (!level.selectedAccount && !level.inputText.trim()) return null;
              const isNewLevel = !level.selectedAccount && level.inputText.trim();
              return (
                <div key={idx} className="coa-summary-row">
                  <span className="coa-summary-label">{COA_LEVEL_LABELS[idx]}</span>
                  <span className="coa-summary-value">
                    {level.selectedAccount
                      ? (level.selectedAccount.coa_id === editingAccount?.coa_id
                          ? `${form.account_number.trim()} ${form.account_name.trim()}`
                          : `${level.selectedAccount.account_number} ${level.inputText.trim()}`)
                      : <>{level.inputText.trim()} <em style={{ color: '#1565c0' }}>(new)</em></>}
                  </span>
                </div>
              );
            })}
            <div className="coa-summary-row">
              <span className="coa-summary-label">Owner</span>
              <span className="coa-summary-value">
                {formOwner ? `${formOwner.display_name} [${formOwner.badge}]` : <em style={{ color: '#777' }}>All (no specific owner)</em>}
              </span>
            </div>
            {form.finsta_cate_id && (
              <div className="coa-summary-row">
                <span className="coa-summary-label">Category</span>
                <span className="coa-summary-value">{resolveCategoryName(Number(form.finsta_cate_id), categories, headers)}</span>
              </div>
            )}
            {form.finsta_sub_cate_id && (
              <div className="coa-summary-row">
                <span className="coa-summary-label">Sub-Category</span>
                <span className="coa-summary-value">
                  {/* eslint-disable-next-line eqeqeq */}
                  {subCategories.find(sc => sc.sub_cate_id == Number(form.finsta_sub_cate_id))?.sub_cate_name || ''}
                </span>
              </div>
            )}
            {form.account_number.trim() && (
              <div className="coa-summary-row">
                <span className="coa-summary-label">Account Number</span>
                <span className="coa-summary-value" style={{ fontFamily: 'monospace' }}>{form.account_number.trim()}</span>
              </div>
            )}
            {form.normal_balance && (
              <div className="coa-summary-row">
                <span className="coa-summary-label">Normal Balance</span>
                <span className="coa-summary-value">{form.normal_balance}</span>
              </div>
            )}
            <div className="coa-summary-row">
              <span className="coa-summary-label">Type</span>
              <span className="coa-summary-value">{form.is_header ? 'Header (grouping)' : 'Detail (postable)'}</span>
            </div>
            {form.is_header !== 1 && (
              <>
                <div className="coa-summary-row">
                  <span className="coa-summary-label">Opening Balance</span>
                  <span className="coa-summary-value" style={{ fontFamily: 'monospace' }}>{formatBalance(form.opening_balance)} {form.currency}</span>
                </div>
                {form.opening_balance_date && (
                  <div className="coa-summary-row">
                    <span className="coa-summary-label">Balance Date</span>
                    <span className="coa-summary-value">{form.opening_balance_date}</span>
                  </div>
                )}
              </>
            )}
            {form.account_description && (
              <div className="coa-summary-row">
                <span className="coa-summary-label">Description</span>
                <span className="coa-summary-value">{form.account_description.trim()}</span>
              </div>
            )}
          </div>
        )}

        {/* ---- Action buttons ---- */}
        <div className="coa-actions">
          {!showSummary ? (
            <button className="coa-btn-primary" onClick={handleReview} disabled={!canReview}
              style={!canReview ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            >
              <FiCheck size={16} /> Review
            </button>
          ) : (
            <button className="coa-btn-primary" onClick={handleSave}>
              <FiCheck size={16} /> {isNewAtAnyLevel ? 'Confirm & Create' : 'Save Changes'}
            </button>
          )}
          <button className="coa-btn-secondary" onClick={closeForm}>Clear</button>
          {isEditing && editingAccount && (
            <button className="coa-btn-danger" onClick={() => openDeleteConfirm(editingAccount)} style={{ marginLeft: 'auto' }}>
              <FiTrash2 size={16} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* ---- Delete Confirmation ---- */}
      {mode === 'delete' && editingAccount && (
        <div className="coa-form-card">
          <div className="coa-form-header">
            <div className="coa-form-title" style={{ color: '#c62828' }}>
              <FiAlertCircle size={18} /> Delete Account
            </div>
            <button className="coa-btn-close" onClick={closeForm}><FiX size={18} /></button>
          </div>

          <div className="coa-delete-warning">
            <div className="coa-delete-warning-title">
              <FiAlertCircle size={16} /> Are you sure you want to delete &ldquo;{editingAccount.account_number} {editingAccount.account_name}&rdquo;?
            </div>
            {deleteChildCount > 0 && (
              <p>This account has <strong>{deleteChildCount}</strong> sub-account{deleteChildCount !== 1 ? 's' : ''} that will also be deleted.</p>
            )}
            {(Number(editingAccount.current_balance) !== 0) && (
              <p style={{ color: '#c62828', fontWeight: 600 }}>
                <FiAlertCircle size={14} style={{ verticalAlign: 'middle' }} /> This account has a balance of {formatBalance(editingAccount.current_balance)} {editingAccount.currency}. Deleting it may affect your books.
              </p>
            )}
            <p>This action will soft-delete the account. It can be recovered from Data &amp; Backups.</p>
          </div>

          <div className="coa-actions" style={{ marginTop: 16 }}>
            <button className="coa-btn-danger" onClick={handleDelete}>
              <FiTrash2 size={16} /> Yes, Delete
            </button>
            <button className="coa-btn-secondary" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================
// Wrapper with sub-tabs: Owner COA vs Client COA
// =============================================
interface GLWrapperProps {
  // When rendered inside a pillar (Your Accounts / Client's Accounts), the
  // owner-vs-client choice is fixed by the pillar and the client is pre-picked.
  scope?: 'self' | 'clients';
  selectedClientId?: number | string | null;
}

const GLChartOfAccountsWrapper: React.FC<GLWrapperProps> = ({ scope, selectedClientId: externalClientId = null }) => {
  const pillarMode = scope != null;
  const [subTab, setSubTab] = useState<'owner' | 'clients'>(scope === 'clients' ? 'clients' : 'owner');
  const [ownerInnerTab, setOwnerInnerTab] = useState<'business_type' | 'setup_gl' | 'gl_accounts' | 'headers'>('business_type');
  const [selectedClientId, setSelectedClientId] = useState<number | string | null>(pillarMode ? externalClientId : null);
  const [subTabResetKey, setSubTabResetKey] = useState(0);
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));

  useEffect(() => {
    loadTable('users').then(setAllUsers).catch(() => {});
  }, []);

  // Keep the wrapper in sync with the pillar's scope + chosen client.
  useEffect(() => {
    if (scope) setSubTab(scope === 'clients' ? 'clients' : 'owner');
  }, [scope]);
  useEffect(() => {
    if (pillarMode) setSelectedClientId(externalClientId);
  }, [pillarMode, externalClientId]);

  const accountOwnerId = useMemo(() => {
    return getAccountOwnerId(allUsers) ?? 0;
  }, [allUsers]);

  const personalClients = useMemo(() => {
    if (!accountOwnerId) return [];
    // eslint-disable-next-line eqeqeq
    return allUsers.filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted != 1 && u.relationship === 'Personal Client');
  }, [accountOwnerId, allUsers]);

  const selectedClient = useMemo(() => {
    if (!selectedClientId) return null;
    // eslint-disable-next-line eqeqeq
    return personalClients.find(c => c.user_id == selectedClientId) || null;
  }, [selectedClientId, personalClients]);

  // Guard-then-land for the inner tabs. Re-clicking the active tab remounts it (reset to landing).
  const selectInner = async (tab: 'business_type' | 'setup_gl' | 'gl_accounts' | 'headers') => {
    if (!(await confirmDiscardChanges())) return;
    setOwnerInnerTab(tab);
    setSubTabResetKey(prev => prev + 1);
  };

  // The entity whose COA we're managing: owner mode vs the picked client.
  const innerViewMode: 'owner' | 'clients' = subTab === 'clients' ? 'clients' : 'owner';
  const showInner = subTab === 'owner' || (subTab === 'clients' && !!selectedClientId);
  // Setup Account Headers is owner-only; if it was active when switching to a client, fall back.
  const effInner = (innerViewMode === 'clients' && ownerInnerTab === 'headers') ? 'business_type' : ownerInnerTab;

  const INNER_TABS: { key: 'business_type' | 'setup_gl' | 'gl_accounts' | 'headers'; label: string; ownerOnly?: boolean }[] = [
    { key: 'business_type', label: 'Type of Business' },
    { key: 'headers', label: 'Setup Account Headers', ownerOnly: true },
    { key: 'setup_gl', label: 'Setup GL Accounts' },
    { key: 'gl_accounts', label: 'Add New GL Account' },
  ];

  return (
    <div>
      {!pillarMode && (
        <div className="coa-wrapper-subtabs">
          <button className={`fb-client-subtab ${subTab === 'owner' ? 'active' : ''}`}
            onClick={async () => {
              if (!(await confirmDiscardChanges())) return;
              if (subTab === 'owner') { setSubTabResetKey(prev => prev + 1); }
              else { setSubTab('owner'); setSelectedClientId(null); }
            }}>
            Your COA Setup
          </button>
          <button className={`fb-client-subtab ${subTab === 'clients' ? 'active' : ''}`}
            onClick={async () => {
              if (!(await confirmDiscardChanges())) return;
              if (subTab === 'clients') { setSelectedClientId(null); setSubTabResetKey(prev => prev + 1); }
              else { setSubTab('clients'); }
            }}>
            Client's COA Setup
          </button>
        </div>
      )}

      {/* ---- Client selector (clients mode, before a client is picked) ---- */}
      {subTab === 'clients' && !selectedClientId && (
        <div className="coa-client-selector">
          <div className="coa-client-selector-header">
            <FiUsers size={20} />
            <h4>Select a Client</h4>
            <p>Choose a personal client to manage their Chart of Accounts. Each client's accounts are kept separate.</p>
          </div>
          {personalClients.length === 0 ? (
            <div className="coa-client-selector-empty">
              <p>No personal clients found. Add clients in <strong>Family &amp; Businesses &gt; Your Clients</strong> first.</p>
            </div>
          ) : (
            <div className="coa-client-selector-list">
              {personalClients.map(c => (
                <button key={c.user_id} className="coa-client-selector-card" onClick={() => setSelectedClientId(c.user_id)}>
                  <div className="coa-client-selector-avatar">
                    {(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}
                  </div>
                  <div className="coa-client-selector-info">
                    <span className="coa-client-selector-name">{c.first_name} {c.last_name}</span>
                    {c.address_city && <span className="coa-client-selector-loc">{c.address_city}{c.address_state ? `, ${c.address_state}` : ''}</span>}
                  </div>
                  <FiChevronRight size={18} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!pillarMode && subTab === 'clients' && selectedClientId && (
        <div className="coa-client-selected-bar">
          <span>Managing COA for: <strong>{selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client'}</strong></span>
          <button className="coa-btn-change-client" onClick={() => setSelectedClientId(null)}>
            <FiX size={14} /> Change Client
          </button>
        </div>
      )}

      {/* ---- Inner sub-tabs (owner mode always; clients mode once a client is picked) ---- */}
      {showInner && (
        <div className="coa-inner-subtabs">
          {INNER_TABS.filter(t => !(t.ownerOnly && innerViewMode === 'clients')).map(t => (
            <button
              key={t.key}
              className={`coa-inner-subtab ${effInner === t.key ? 'active' : ''}`}
              onClick={() => selectInner(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ---- Inner tab content ---- */}
      {showInner && effInner === 'business_type' && (
        <TypeOfBusinessTab
          key={`bt-${innerViewMode}-${selectedClientId || 'own'}-${subTabResetKey}`}
          viewMode={innerViewMode}
          selectedClientId={innerViewMode === 'clients' ? selectedClientId : null}
        />
      )}

      {showInner && effInner === 'setup_gl' && (
        <SetupGlAccountsTab
          key={`sg-${innerViewMode}-${selectedClientId || 'own'}-${subTabResetKey}`}
          viewMode={innerViewMode}
          selectedClientId={innerViewMode === 'clients' ? selectedClientId : null}
        />
      )}

      {showInner && effInner === 'gl_accounts' && (
        <GLChartOfAccountsTab
          key={`gl-${innerViewMode}-${selectedClientId || 'own'}-${subTabResetKey}`}
          viewMode={innerViewMode}
          selectedClientId={innerViewMode === 'clients' ? selectedClientId : null}
        />
      )}

      {showInner && effInner === 'headers' && innerViewMode === 'owner' && (
        <SetupHeadersTab key={`owner-headers-${subTabResetKey}`} />
      )}
    </div>
  );
};

export { GLChartOfAccountsTab };
export default GLChartOfAccountsWrapper;
